export type AdminClient = {
  id: number;
  name: string;
  slug: string;
};

export type AdminAccount = {
  id: number;
  name: string;
  email: string;
  client: AdminClient;
};

export type LoginResponse = {
  accessToken: string;
  account: AdminAccount;
};

const TOKEN_KEY = "neural_os_access_token";
const ACCOUNT_KEY = "neural_os_account";

export const getApiBaseUrl = () =>
  (process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000/api").replace(/\/$/, "");

export const getAccessToken = () => window.localStorage.getItem(TOKEN_KEY);

const isTokenExpired = (token: string) => {
  try {
    const [, payload] = token.split(".");
    const decoded = JSON.parse(window.atob(payload)) as { exp?: number };
    return typeof decoded.exp !== "number" || decoded.exp * 1000 <= Date.now();
  } catch {
    return true;
  }
};

export const getStoredAccount = (): AdminAccount | null => {
  const rawAccount = window.localStorage.getItem(ACCOUNT_KEY);
  if (!rawAccount) return null;

  try {
    return JSON.parse(rawAccount) as AdminAccount;
  } catch {
    return null;
  }
};

export const isAdminAuthenticated = () => {
  const token = getAccessToken();
  if (!token || isTokenExpired(token) || !getStoredAccount()) {
    clearAuthSession();
    return false;
  }

  return true;
};

export const storeAuthSession = (response: LoginResponse) => {
  window.localStorage.setItem(TOKEN_KEY, response.accessToken);
  window.localStorage.setItem(ACCOUNT_KEY, JSON.stringify(response.account));
  window.localStorage.removeItem("neural_os_auth");
};

export const clearAuthSession = () => {
  window.localStorage.removeItem(TOKEN_KEY);
  window.localStorage.removeItem(ACCOUNT_KEY);
  window.localStorage.removeItem("neural_os_auth");
};

export const authFetch = async (path: string, options?: RequestInit) => {
  const token = getAccessToken();
  const response = await fetch(`${getApiBaseUrl()}${path}`, {
    ...options,
    headers: {
      ...(options?.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options?.headers ?? {}),
    },
  });

  if (response.status === 401) {
    clearAuthSession();
  }

  return response;
};

export const loginAdmin = async (email: string, password: string) => {
  const response = await fetch(`${getApiBaseUrl()}/auth/login/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => null);
    throw new Error(data?.detail || "로그인에 실패했습니다.");
  }

  const data = (await response.json()) as LoginResponse;
  storeAuthSession(data);
  return data;
};
